import { createContext, useContext, useEffect, useState } from "react";
import { getSystemTheme, resolveTheme, type ThemePreference, type ResolvedTheme } from "@/lib/theme";

interface ThemeContextType {
  theme: ResolvedTheme;
  themePreference: ThemePreference;
  setTheme: (theme: ThemePreference, onError?: () => void) => void;
  applyTheme: (theme: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  themePreference: "system",
  setTheme: () => {},
  applyTheme: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("padpulse-theme");
      if (stored === "light" || stored === "dark" || stored === "system") return stored;
    }
    return "system";
  });

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? "dark" : "light");
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, []);

  const theme: ResolvedTheme = themePreference === "system" ? systemTheme : themePreference;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("padpulse-theme", themePreference);
  }, [theme, themePreference]);

  const applyTheme = (next: ThemePreference) => {
    setThemePreferenceState(next);
  };

  const setTheme = (next: ThemePreference, onError?: () => void) => {
    const prev = themePreference;
    setThemePreferenceState(next);
    fetch("/api/app/profile", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preferredTheme: next }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to save theme");
      })
      .catch(() => {
        setThemePreferenceState(prev);
        onError?.();
      });
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, themePreference, setTheme, applyTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
